// ============================================================================
// JARVISTRAVEL - LINK LABEL COMPONENT
// Standardized LINK label for map/booking/phone/website/email deep links.
// Complies with JT-CORE-007: Output format standard — LINK/deep link labels.
// Icons are lucide (no emoji); colors use semantic tokens (dark-mode aware).
// ============================================================================

import { MapPin, ExternalLink, Phone, Globe, Mail, type LucideIcon } from 'lucide-react';
import { safeHref } from '@/app/utils/safeUrl';
import { stopLinkKind } from '@/app/utils/stopLink';
import type { Activity } from '@/app/types';

// -----------------------------------------------------------------------------
// CONFIG
// -----------------------------------------------------------------------------

interface LinkTypeConfig {
  Icon: LucideIcon;
  defaultLabel: string;
  color: string;
}

const LINK_TYPES: Record<string, LinkTypeConfig> = {
  map: {
    Icon: MapPin,
    defaultLabel: 'Map',
    color: 'text-accent bg-accent/10 hover:bg-accent/15',
  },
  booking: {
    Icon: ExternalLink,
    defaultLabel: 'Provider',
    color: 'text-success bg-success/10 hover:bg-success/15',
  },
  phone: {
    Icon: Phone,
    defaultLabel: 'Call',
    color: 'text-accent bg-accent/10 hover:bg-accent/15',
  },
  website: {
    Icon: Globe,
    defaultLabel: 'Website',
    color: 'text-accent bg-accent/10 hover:bg-accent/15',
  },
  email: {
    Icon: Mail,
    defaultLabel: 'Email',
    color: 'text-accent bg-accent/10 hover:bg-accent/15',
  },
};

// -----------------------------------------------------------------------------
// PROPS
// -----------------------------------------------------------------------------

export interface LinkLabelProps {
  /** Link type — determines icon, color, and default label */
  type: 'map' | 'booking' | 'phone' | 'website' | 'email';
  /** Target URL */
  href: string;
  /** Optional custom label (defaults to type's label) */
  label?: string;
  /** Optional click handler to override default navigation */
  onClick?: (e: React.MouseEvent) => void;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Extra classes */
  className?: string;
  /** Open in new tab (default: true) */
  newTab?: boolean;
  /** Render the icon alone (no text), as a round hyperlinked icon button. */
  iconOnly?: boolean;
}

// -----------------------------------------------------------------------------
// COMPONENT
// -----------------------------------------------------------------------------

export function LinkLabel({
  type,
  href,
  label,
  onClick,
  size = 'sm',
  className = '',
  newTab = true,
  iconOnly = false,
}: LinkLabelProps) {
  const config = LINK_TYPES[type];
  if (!config) return null;

  const { Icon } = config;
  const resolvedLabel = label || config.defaultLabel;
  const sizeStyles = size === 'md'
    ? 'px-3 py-1.5 text-sm gap-1.5'
    : 'px-2 py-1 text-xs gap-1';
  const iconSize = size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';

  const linkProps = newTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  // Icon-only: a round hyperlinked icon (the destination is conveyed by the icon
  // + aria-label, never the raw URL / phone number).
  if (iconOnly) {
    return (
      <a
        href={safeHref(href)}
        {...linkProps}
        onClick={onClick}
        aria-label={resolvedLabel}
        title={resolvedLabel}
        className={`inline-flex items-center justify-center rounded-full transition-colors ${config.color} ${size === 'md' ? 'w-9 h-9' : 'w-8 h-8'} ${className}`}
      >
        <Icon className={iconSize} strokeWidth={2} aria-hidden="true" />
      </a>
    );
  }

  return (
    <a
      href={safeHref(href)}
      {...linkProps}
      onClick={onClick}
      className={`inline-flex items-center font-medium rounded-lg transition-colors ${config.color} ${sizeStyles} ${className}`}
    >
      <Icon className={`flex-shrink-0 ${iconSize}`} strokeWidth={2} aria-hidden="true" />
      <span>{resolvedLabel}</span>
    </a>
  );
}

/** A stop's link (JAR-2450): the place's own website, or its page on a booking
 *  site or the Explore provider it was added from. Nothing when it has none. */
export function StopLink({ activity, ...props }: { activity: Pick<Activity, 'providerUrl' | 'linkKind'> } & Omit<LinkLabelProps, 'type' | 'href' | 'label'>) {
  if (!activity.providerUrl) return null;
  return stopLinkKind(activity) === 'website'
    ? <LinkLabel type="website" href={activity.providerUrl} label="Website" {...props} />
    : <LinkLabel type="booking" href={activity.providerUrl} label="Provider site" {...props} />;
}
